import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useRecoilState, useSetRecoilState } from 'recoil';
import styled from 'styled-components';

import useIntersect from '@/hooks/useIntersect';
import { hasAlertSelector } from '@/recoil';
import { alarmArr } from '@/recoil/notification';
import LabelUsingAffixIcon from '@/components/ContextPrinter/LabelUsingAffixIcon';
import DividerDefault from '@/components/Divider/Default';
import { socket } from '@/utils/notification/Socket';
import { FontStyle } from '@/utils/StyleUtil';
import StringUtil from '@/utils/StringUtil';
import {
    COMMUNITY_ANSWER,
    COMMUNITY_ANSWER_EXPIRED,
    IAlarmItem,
    IAllAlarm,
    MYPLAN_PROPOSAL,
    MYPLAN_PROPOSAL_CHECK,
    ONE_TO_ONE_MATCHING,
} from '@/models';
import { TABLET, RESIZED_SCREEN_TABLET } from '@/models/breakPoints';
import useAlarmEvent from '@/hooks/useAlarmEvent';
import { API, URLs } from '@/api';
import { GetNotificationList } from '@/queries/myPage/notification/notification';

const AlarmList = () => {
    const [notifications, setNotifications] = useRecoilState(alarmArr);
    const fetchedAll = useRef(false);
    const hasAlert = useSetRecoilState(hasAlertSelector);

    //console.log('AlarmList isSuccess', isSuccess);
    //setNotifications(notificationsRefined);
    // console.log('Is infinite hook called', data);
    //const [bookmark] = useState(notifications.metadata.unreadCount);

    const alarmEvent = useAlarmEvent();

    useEffect(() => {
        //fetchNextPage();
        //console.log('rawData after fetchNext', rawData);
        const checkPatch = async () => {
            // calling patch api here

            await API.patch(URLs.noti.notificationCheck);
        };

        // console.log('Checking infiniteData', rawData);

        GetNotificationList().then(res => {
            //const notificaitonListData = notifications as unknown as IAllAlarm;
            setNotifications(res);
        });

        if (notifications.data.length && notifications.data[0]) {
            // change this to PATCH api call
            // socket.bookmark();

            checkPatch();

            // setNotifications(notification => {
            //     return {
            //         data: notification.data,
            //         metadata: {
            //             unreadCount: notification.metadata.unreadCount,
            //         },
            //     };
            // });
            hasAlert(false);

            if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify({ unReadNotiCnt: 0 }));
        }
    }, []);

    const handleIntersect = useIntersect(() => {
        if (!fetchedAll.current && notifications.data.length > 0 && notifications.data.length % 20 === 0) {
            // console.log('Reached intersect', hasNextPage);
            const nextPage = notifications.data.length / 20 + 1;
            // call next page here
            //socket.status(nextPage);
            GetNotificationList(nextPage).then(res => {
                //const notificaitonListData = notifications as unknown as IAllAlarm;
                const messages = res.data;
                const unreadCount = res.metadata.unreadCount;

                setNotifications((notifications: IAllAlarm) => {
                    return {
                        data: [...notifications.data, ...messages],
                        metadata: {
                            unreadCount: notifications.metadata.unreadCount,
                        },
                    };
                });
            });
        } else if (notifications.data.length >= 20 && !fetchedAll.current) {
            const addedWhileOnNotficationPage = notifications.data.length % 20;
            const nextPage = Math.floor(notifications.data.length / 20) + 1;
            GetNotificationList(nextPage).then(res => {
                const messages = res.data.slice(addedWhileOnNotficationPage);
                setNotifications((notifications: IAllAlarm) => {
                    return {
                        data: [...notifications.data, ...messages],
                        metadata: {
                            unreadCount: notifications.metadata.unreadCount,
                        },
                    };
                });
                if (messages.length < 20) fetchedAll.current = true;
            });
        }
    });

    const setIcon = (type: number) => {
        if (type === COMMUNITY_ANSWER || type === COMMUNITY_ANSWER_EXPIRED) return 'qna_notice';
        else if (type === MYPLAN_PROPOSAL || type === MYPLAN_PROPOSAL_CHECK || type === ONE_TO_ONE_MATCHING)
            return 'proposal_notice';
        else return 'notice';
    };

    const renderList = () => {
        if (notifications.data.length > 0) {
            return notifications.data.map((item: IAlarmItem, index: number) => {
                return (
                    <div key={index}>
                        <NotiItemContainer key={index} onClick={() => alarmEvent(item.type, item.payload)}>
                            <LabelUsingAffixIcon
                                prefixUri={
                                    <IconWrapper>
                                        <Image
                                            src={`/img/notification/${setIcon(item.type)}@3x.png`}
                                            alt="notice icon"
                                            width={18}
                                            height={18}
                                        />
                                    </IconWrapper>
                                }
                                label={item.message}
                                labelStyle={{
                                    width:
                                        window && window.innerWidth >= TABLET
                                            ? `calc(${RESIZED_SCREEN_TABLET}px - 96px)`
                                            : 'calc(100vw - 96px)',
                                    marginRight: '10px',
                                    wordBreak: 'break-word',
                                }}
                                suffixUri={!item.isRead && <NonReadIcon />}
                            />
                            <div className="created_at">{StringUtil.displayedAt(item.receivedAt)}</div>
                        </NotiItemContainer>
                        {index !== notifications.data.length - 1 && <DividerDefault thickness={1} color="#f1f1f1" />}
                    </div>
                );
            });
        }
        return <NoAlarm>알람이 없습니다.</NoAlarm>;
    };

    return (
        <Container>
            {renderList()}
            <TargetOfInfiniteScroll ref={handleIntersect} />
        </Container>
    );
};

const Container = styled.div`
    min-height: 100%;
    padding: 0 20px;
    background-color: #ffffff;
`;

const NotiItemContainer = styled.div`
    padding: 18px 10px 18px 0;

    .created_at {
        margin: 12px 0 0 26px;
        ${FontStyle(12, 20, '400', '#c8c8c8', -1)};
    }
`;

const IconWrapper = styled.div`
    ${({ theme }) => theme.flex.flexOnly}
    ${({ theme }) => theme.flex.justifyCenter}
    min-width: 18px;
    min-height: 18px;
    margin-right: 3px;
`;

const NonReadIcon = styled.div`
    min-width: 5px;
    min-height: 5px;
    border-radius: 5px;
    background-color: red;
`;

const NoAlarm = styled.div`
    ${({ theme }) => theme.flex.flexOnly};
    ${({ theme }) => theme.flex.justifyCenter};
    width: 100%;
    height: calc(100vh - 152px);
    ${FontStyle(14, 22, '400', '#acacac', -1)};
`;

const TargetOfInfiniteScroll = styled.div`
    height: 1px;
`;

export default AlarmList;
